import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayDate } from '../common/datePresentation';
import { formatLedgerAmount } from '../manage/clientLedgerTypes';
import SettlementExtraFeeForm from './SettlementExtraFeeForm';

export default function SettlementExtraFees({ settlement, state, students, onMutate, initialStudentId, onClose }) {
  const { t, i18n } = useTranslation();
  const tr = key => t(`billing:fees.${key}`);
  const [editing, setEditing] = useState({});
  const [formVersion, setFormVersion] = useState(0);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const request = useRef(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const items = state ? state.items : (settlement.snapshot.extraFees || []);
  async function mutate(action, fee, feeId, correctionReason) {
    if (inFlight.current) throw new Error('FEE_SAVE_IN_PROGRESS');
    inFlight.current = true; setBusy(true);
    const body = { action, fee, feeId, correctionReason, expectedBillingRevision: state.expectedBillingRevision };
    const key = JSON.stringify(body);
    if (!request.current || request.current.key !== key) request.current = { key, id: `fee-${Date.now()}-${Math.random().toString(36).slice(2)}` };
    try {
      await onMutate({ ...body, requestId: request.current.id });
      request.current = null;
      if (mounted.current) { setEditing({}); setFormVersion(value => value + 1); setPending(null); setError(''); }
    } finally { if (mounted.current) setBusy(false); inFlight.current = false; }
  }
  async function confirm() {
    if (inFlight.current) return;
    if (settlement.approved && (!reason.trim() || reason.length > 1000)) { setError(tr('invalidReason')); return; }
    setError('');
    try { await mutate(pending.action, undefined, pending.feeId, reason.trim()); } catch (failure) { if (mounted.current) setError(tr(failure.feesRefreshed ? 'conflict' : 'saveError')); }
  }
  return (
    <Modal
      show
      centered
      size="lg"
      onHide={() => { if (!inFlight.current) onClose(); }}
      aria-labelledby="settlement-fees-title"
      onEntered={node => node.removeAttribute('aria-hidden')}
    >
      <Modal.Header closeButton closeLabel={tr('cancel')}>
        <Modal.Title id="settlement-fees-title">{tr('title')}: {settlement.clientName} - {settlement.month}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className="settlement-extra-fees">
          {state && state.hasDraft && <small className="text-muted">{tr('draft')}</small>}
          {!state && <p className="text-muted small">{tr('loading')}</p>}
          {editing && state && (
          <SettlementExtraFeeForm
            key={editing.feeId || `new-${formVersion}`}
            month={settlement.month}
            students={students}
            initialStudentId={initialStudentId}
            initialFee={editing.feeId ? editing : null}
            idPrefix={`fee-${settlement.clientId}`}
            correctionRequired={settlement.approved}
            initialReason={state.correctionReason || ''}
            onCancel={() => { if (editing.feeId) { setEditing({}); setFormVersion(value => value + 1); } else onClose(); }}
            onSave={(fee, correctionReason) => mutate(editing.feeId ? 'update' : 'add', fee, editing.feeId, correctionReason)}
          />
          )}
          {!!items.length && (
          <div className="table-responsive mt-4">
            <table className="table table-sm manage-text">
              <thead className="table-header manage-table-title">
                <tr>
                  <th>{tr('date')}</th><th>{tr('description')}</th><th>{tr('student')}</th><th>{tr('amount')}</th><th>{t('manage:actions_134')}</th>
                </tr>
              </thead>
              <tbody>{items.map(item => (
                <tr key={item.feeId}>
                  <td className="text-nowrap">{displayDate(item.date, i18n.language)}</td>
                  <td>{item.description}<small className="d-block text-muted">{tr(item.category)}</small></td>
                  <td>{item.studentNameSnapshot || tr('wholeClient')}</td>
                  <td className="text-nowrap">{formatLedgerAmount(item.amountCents / 100, i18n.language)}</td>
                  <td>{state && (
                  <>
                    <button type="button" className="btn btn-link btn-sm p-0 mr-2" disabled={busy} onClick={() => { setEditing(item); setPending(null); }}>{tr(settlement.approved ? 'correct' : 'edit')}</button>
                    <button type="button" className="btn btn-link btn-sm p-0 text-danger" disabled={busy} onClick={() => { setPending({ action: 'remove', feeId: item.feeId }); setEditing(null); }}>{tr(settlement.approved ? 'withdraw' : 'remove')}</button>
                  </>
                  )}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          )}
          {!items.length && <p className="small text-muted mb-2">{tr('empty')}</p>}
          {state && state.hasDraft && <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => { setPending({ action: 'discardDraft' }); setEditing(null); }}>{tr('discard')}</button>}
          {pending && (
          <div className="border p-3 mt-2">
            <p>{tr(pending.action === 'remove' ? 'removeConfirm' : 'discardConfirm')}</p>
            {settlement.approved && (
            <label className="d-block" htmlFor={`fee-${settlement.clientId}-withdraw-reason`}>{tr('reason')}
              <input id={`fee-${settlement.clientId}-withdraw-reason`} className="form-control" value={reason} onChange={event => setReason(event.target.value)} />
            </label>
            )}
            {error && <div className="text-danger" role="alert">{error}</div>}
            <button type="button" className="btn manage-teal-button mr-2 mb-2" disabled={busy} onClick={() => { setPending(null); setEditing({}); }}>{tr('cancel')}</button>
            <button type="button" className="btn manage-red-button mb-2" disabled={busy} onClick={confirm}>{tr(busy ? 'saving' : 'confirm')}</button>
          </div>
          )}
          <small className="text-muted d-block mt-2">{tr('hint')}</small>
        </section>
      </Modal.Body>
    </Modal>
  );
}
SettlementExtraFees.propTypes = {
  settlement: PropTypes.object.isRequired,
  state: PropTypes.object,
  students: PropTypes.array.isRequired,
  onMutate: PropTypes.func.isRequired,
  initialStudentId: PropTypes.string,
  onClose: PropTypes.func,
};
SettlementExtraFees.defaultProps = { state: null, initialStudentId: null, onClose: () => {} };
